import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react'

import { BrandRefreshBetaBanner } from './BrandRefreshBetaBanner'
import { useBrandRefreshBetaMarketingThemeControl } from './brandRefreshBetaMarketingThemeContext'
import { BrandRefreshIntroModal } from './BrandRefreshIntroModal'
import { BrandRefreshTryConfirmModal } from './BrandRefreshTryConfirmModal'
import {
  BRAND_REFRESH_EXPIRED_OPT_IN_BASE_DELAY_MS,
  BRAND_REFRESH_EXPIRED_OPT_IN_MAX_RETRIES,
  BRAND_REFRESH_OPT_IN_SOURCES,
} from './constants'
import { useBrandRefreshBannerCountdown } from './useBrandRefreshBannerCountdown'
import { useBrandRefreshBetaEligibility } from './useBrandRefreshBetaEligibility'
import { useOptInToBrandRefresh } from './useOptInToBrandRefresh'
import { useRecordBrandRefreshBannerStartedAt } from './useRecordBrandRefreshBannerStartedAt'
import { useProductRefreshGlobalBetaEnabled } from '../product_refresh/useProductRefreshGlobalBetaEnabled'
import { useProductRefreshUserOptIn } from '../product_refresh/useProductRefreshUserOptIn'

const clearExpiredOptInRetryTimeout = (
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
) => {
  if (timeoutRef.current != null) {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }
}

/**
 * Global early-access chrome: in-flow banner (starts the 7-day window) plus optional
 * intro / confirm modals. The intro is user-initiated from the banner — not blocking on load.
 */
export const BrandRefreshBetaGlobalHeader = () => {
  const isEligible = useBrandRefreshBetaEligibility()
  const { optIn } = useOptInToBrandRefresh()
  const hasGlobalBeta = useProductRefreshGlobalBetaEnabled(true)
  const userOptedIn = useProductRefreshUserOptIn()
  const isVisible = isEligible
  const { daysRemaining, isExpired, isReady } = useBrandRefreshBannerCountdown(
    isVisible,
    hasGlobalBeta,
  )

  useRecordBrandRefreshBannerStartedAt(isVisible && hasGlobalBeta)
  const { setIsActive: setMarketingThemeActive } = useBrandRefreshBetaMarketingThemeControl()

  const [showIntro, setShowIntro] = useState(false)
  const [showTryConfirm, setShowTryConfirm] = useState(false)
  const hasForcedOptInRef = useRef(false)
  const forcedOptInInFlightRef = useRef(false)
  const expiredOptInAttemptRef = useRef(0)
  const expiredOptInRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const openIntro = useCallback(() => {
    setShowIntro(true)
  }, [])

  const openTryConfirm = useCallback(() => {
    setShowTryConfirm(true)
  }, [])

  const closeIntro = useCallback(() => {
    setShowIntro(false)
  }, [])

  useEffect(() => {
    setMarketingThemeActive(isVisible || showIntro || showTryConfirm)
    return () => setMarketingThemeActive(false)
  }, [isVisible, showIntro, showTryConfirm, setMarketingThemeActive])

  useEffect(() => {
    if (userOptedIn) {
      return
    }

    hasForcedOptInRef.current = false
    expiredOptInAttemptRef.current = 0
    clearExpiredOptInRetryTimeout(expiredOptInRetryTimeoutRef)
  }, [userOptedIn])

  useEffect(() => {
    if (
      !isVisible
      || !hasGlobalBeta
      || !isReady
      || !isExpired
      || hasForcedOptInRef.current
    ) {
      return undefined
    }

    let cancelled = false
    expiredOptInAttemptRef.current = 0

    const runForcedOptIn = async () => {
      if (cancelled || hasForcedOptInRef.current || forcedOptInInFlightRef.current) {
        return
      }

      forcedOptInInFlightRef.current = true

      try {
        await optIn(BRAND_REFRESH_OPT_IN_SOURCES.COUNTDOWN_EXPIRED)
        if (!cancelled) {
          hasForcedOptInRef.current = true
        }
      } catch {
        if (cancelled || hasForcedOptInRef.current) {
          return
        }

        const failedAttempt = expiredOptInAttemptRef.current
        if (failedAttempt >= BRAND_REFRESH_EXPIRED_OPT_IN_MAX_RETRIES) {
          return
        }

        expiredOptInAttemptRef.current = failedAttempt + 1
        const delayMs = BRAND_REFRESH_EXPIRED_OPT_IN_BASE_DELAY_MS * 2 ** failedAttempt

        clearExpiredOptInRetryTimeout(expiredOptInRetryTimeoutRef)
        expiredOptInRetryTimeoutRef.current = setTimeout(() => {
          expiredOptInRetryTimeoutRef.current = null
          runForcedOptIn()
        }, delayMs)
      } finally {
        forcedOptInInFlightRef.current = false
      }
    }

    runForcedOptIn()

    return () => {
      cancelled = true
      clearExpiredOptInRetryTimeout(expiredOptInRetryTimeoutRef)
    }
  }, [hasGlobalBeta, isExpired, isReady, isVisible, optIn])

  if (!isVisible) {
    return null
  }

  return (
    <>
      <BrandRefreshBetaBanner
        daysRemaining={daysRemaining}
        onTryItNow={openIntro}
      />
      {showIntro && (
        <BrandRefreshIntroModal
          daysRemaining={daysRemaining}
          onClose={() => {
            closeIntro()
          }}
          onTryItNow={() => {
            closeIntro()
            openTryConfirm()
          }}
        />
      )}
      {showTryConfirm && (
        <BrandRefreshTryConfirmModal onClose={() => setShowTryConfirm(false)} />
      )}
    </>
  )
}
